import '@laura/design-system/styles.css';
import { ErrorBoundary } from '@laura/design-system';
import { createQueryClient, lazyAuth, SessionProvider, warmFonts } from '@laura/portal-kit';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { lazyPages, preloadFor } from './routes.js';
import { loadConfig } from './config.js';

// The module being opened downloads while config and sign-in load (plan 45 phase 2).
void preloadFor(window.location.pathname === '/' ? '/clinics' : window.location.pathname)?.().catch(
  () => undefined,
);
warmFonts();
const config = await loadConfig();
// Amplify loads after the first page renders (lazyAuth, plan 45 phase 2).
const backend = lazyAuth({
  userPoolId: config.userPoolId,
  clientId: config.clientId,
  // Staff tokens live only as long as the tab (ADR 040).
  storage: 'session',
  // ADR 160: TOTP first, with an emailed code as the fallback.
  mfa: 'totp-first',
});

// One query policy (plan 45 phase 2). The back office has no live queue
// screens: nothing refetches on window focus; Health and Monitoring poll.
const queryClient = createQueryClient({
  focusKeys: [],
  settingsKeys: ['me', 'flags', 'plans', 'templates', 'test-recipients', 'terms'],
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <ErrorBoundary label="The back office">
      <QueryClientProvider client={queryClient}>
        <SessionProvider backend={backend} apiUrl={config.apiUrl}>
          <BrowserRouter>
            <App pages={lazyPages()} />
          </BrowserRouter>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
